import {identifyPostHog, isPostHogIdentified, isSensitiveAnalyticsRoute, resetPostHog} from '@/lib/posthog';
import {useAuth} from '@/contexts/AuthContext';
import {useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

function getPostHogClient(): Window['posthog'] | null {
    if (typeof window === 'undefined') {
        return null;
    }
    const candidate = window.posthog;
    if (!candidate || typeof candidate !== 'object') {
        return null;
    }
    return candidate;
}

function registerProduct(): void {
    try {
        window.posthog?.register?.({product: 'tribetale'});
    } catch {
        // Analytics must never break identity transitions.
    }
}

export function PostHogIdentity() {
    const {user, isLoading} = useAuth();
    const location = useLocation();
    const lastIdentifiedUserId = useRef<string | null>(null);
    const userId = user?.id ? String(user.id) : null;
    const routeKey = `${location.pathname}${location.search}${location.hash}`;

    useEffect(() => {
        try {
            const client = getPostHogClient();
            if (!client) {
                return;
            }

            const currentRoute = typeof window === 'undefined' ? routeKey : window.location.href;
            if (isSensitiveAnalyticsRoute(currentRoute)) {
                client.set_config?.({disable_session_recording: true});
                client.stopSessionRecording?.();
                return;
            }

            if (import.meta.env.VITE_POSTHOG_SESSION_REPLAY === 'true') {
                client.set_config?.({disable_session_recording: false});
                return;
            }

            client.set_config?.({disable_session_recording: true});
            client.stopSessionRecording?.();
        } catch {
            // Analytics privacy guards must never break navigation.
        }
    }, [routeKey]);

    useEffect(() => {
        // Wait for auth to resolve before treating an absent user as logged out.
        if (isLoading) {
            return;
        }

        if (!userId) {
            if (lastIdentifiedUserId.current !== null) {
                resetPostHog();
                registerProduct();
                lastIdentifiedUserId.current = null;
                return;
            }

            let cancelled = false;
            let attempts = 0;
            let retryId: number | undefined;

            function resetWhenReady() {
                if (cancelled) {
                    return;
                }

                if (isPostHogIdentified()) {
                    resetPostHog();
                    registerProduct();
                    lastIdentifiedUserId.current = null;
                    return;
                }

                const client = getPostHogClient() as (Window['posthog'] & {_isIdentified?: unknown}) | null;
                if (typeof client?._isIdentified === 'function') {
                    return;
                }

                attempts += 1;
                if (attempts < 20) {
                    retryId = window.setTimeout(resetWhenReady, 250);
                }
            }

            resetWhenReady();

            return () => {
                cancelled = true;
                if (retryId !== undefined) {
                    window.clearTimeout(retryId);
                }
            };
        }

        if (lastIdentifiedUserId.current === userId) {
            return;
        }

        const distinctId = userId;
        let cancelled = false;
        let attempts = 0;
        let retryId: number | undefined;

        function identifyWhenReady() {
            if (cancelled) {
                return;
            }

            const client = getPostHogClient();
            if (client?.identify) {
                try {
                    const previousUserId = lastIdentifiedUserId.current;
                    if (previousUserId && previousUserId !== distinctId) {
                        resetPostHog();
                        registerProduct();
                    }
                    identifyPostHog(distinctId);
                    lastIdentifiedUserId.current = distinctId;
                } catch {
                    // Analytics must never break the authenticated app shell.
                }
                return;
            }

            attempts += 1;
            if (attempts < 20) {
                retryId = window.setTimeout(identifyWhenReady, 250);
            }
        }

        identifyWhenReady();

        return () => {
            cancelled = true;
            if (retryId !== undefined) {
                window.clearTimeout(retryId);
            }
        };
    }, [userId, isLoading]);

    return null;
}
